import { useState, useEffect, useRef } from "react";
import { Table } from "react-bootstrap";
import { getLeadConfig, setLeadConfig, deleteLeadConfig } from "../../../api/lead";
import { Toast, Modal } from "bootstrap"; 

const LeadSettings = () => {
  const [loadingData, setLoadingData] = useState(false);
  const [configuration, setConfiguration] = useState();
  const statusModalRef = useRef(null);
  const leadTypeModalRef = useRef(null);
  const sourceModalRef = useRef(null);
  const followupModalRef = useRef(null);
  const bucketModalRef = useRef(null);
  const priorityModalRef = useRef(null);
  const cityModalRef = useRef(null);
  const countryModalRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null); 
  const [currentType, setCurrentType] = useState(null); 

  const toastRef = useRef(null);
  const [formData, setFormData] = useState({
    lead_status: { name: "", description: "" },
    lead_types: { name: "", description: "" },
    lead_sources: { name: "", priority: "", bucket: "", description: "" },
    followup_type: { name: "", priority: "", description: "" },
    buckets: { name: "", lead_types: "", source_ids: "", description: "" },
    priority: { name: "", description: "" },
    city: { name: "", country: "", is_active: true },
    country: { name: "", is_active: true },
  });

  const fetchData = async () => {
    setLoadingData(true);
    try {
      let types =
        "buckets,lead_types,lead_sources,city,country,followup_type,priority,lead_status";
      const configData = await getLeadConfig(types);
      if (configData.status) {
        setConfiguration(configData.data);
        setLoadingData(false);
      }
    } catch (error) {
      console.log("Error fetching data: ", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e, section) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: value,
      },
    }));
  };

  const handleModalSubmit = async (type, modalRef) => {
    const modalElement = modalRef?.current;
    if (!modalElement) {
      console.warn(`${type} modal ref is not available`);
      return;
    }
  
    const data = formData[type];
    const payload = isEditing ? { ...data, id: editingId } : data;
  
    const response = await setLeadConfig(type, payload, isEditing);
  
    if (response.status === true && toastRef.current) {
      new Toast(toastRef.current).show();
      await fetchData();
  
      const modalInstance =
        Modal.getInstance(modalElement) || new Modal(modalElement);
  
      modalInstance.hide();
  
      modalInstance.hide();

      modalElement.addEventListener(
        "hidden.bs.modal",
        () => {
          document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

          document.body.classList.remove("modal-open");
          document.body.style.overflow = "auto";
          document.body.style.removeProperty("padding-right");

          modalInstance.dispose();
        },
        { once: true }
      );
  
      const emptyFields = Object.fromEntries(
        Object.entries(formData[type]).map(([key, value]) => {
          if (typeof value === "boolean") return [key, true];
          return [key, ""];
        })
      );
  
      setFormData((prev) => ({
        ...prev,
        [type]: emptyFields,
      }));
  
      setIsEditing(false);
      setEditingId(null);
    }
  };   

  const handleEditClick = (type, data) => {
    setCurrentType(type);
    setIsEditing(true);
    setEditingId(data.id);

    setFormData((prev) => ({
      ...prev,
      [type]: Object.fromEntries(
        Object.entries(prev[type]).map(([key]) => [key, data[key] ?? ""])
      ),
    }));
  };

  const handleDeleteClick = async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
  
    const response = await deleteLeadConfig(type, id);
  
    if (response.status === true && toastRef.current) {
      new Toast(toastRef.current).show();
      await fetchData(); // refresh table after delete
    }
  };
  
  const handleAddClick = (type) => {
    setCurrentType(type);
    setIsEditing(false);
    setEditingId(null);

    setFormData((prev) => ({
      ...prev,
      [type]: Object.fromEntries(
        Object.entries(prev[type]).map(([key]) => [key, key === "is_active" ? true : ""])
      ),
    }));
  };

  return loadingData ? (
    <div className="d-flex justify-content-center">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  ) : (
    <>
      <div className="row mb-3 mt-5">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-primary primary-button"
              data-bs-toggle="modal"
              data-bs-target="#statusModal"
            >
              Add Status
            </button>
            <span className="badge bg-secondary">Status List</span>
          </div>
          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Status Name</th>
                <th>Status Id</th>
                <th>Description</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {configuration?.lead_status?.map((bucket, index) => (
                <tr key={index}>
                  <td>{bucket?.name}</td>
                  <td>{bucket?.id}</td>
                  <td>{bucket?.description}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      data-bs-toggle="modal"
                      data-bs-target="#statusModal"
                      onClick={() => handleEditClick("lead_status",bucket)}
                    >
                      Edit
                    </button>
                    <button type="button" class="btn btn-danger btn-sm" title="Delete"
                      onClick={() => handleDeleteClick("lead_status", bucket.id)}>
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="col">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-primary primary-button"
              data-bs-toggle="modal"
              data-bs-target="#sourceModal"
            >
              Add Source
            </button>
            <span className="badge bg-secondary">Source List</span>
          </div>
          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Source Name</th>
                <th>Source Id</th>
                <th>Priority</th>
                <th>Bucket</th>
                <th>Description</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {configuration?.lead_sources?.map((bucket, index) => (
                <tr key={index}>
                  <td>{bucket?.name}</td>
                  <td>{bucket?.id}</td>
                  <td>{bucket?.priority}</td>
                  <td>{bucket?.bucket}</td>
                  <td>{bucket?.description}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      data-bs-toggle="modal"
                      data-bs-target="#sourceModal"
                      onClick={() => handleEditClick("lead_sources",bucket)}
                    >
                      Edit
                    </button>
                    <button type="button" class="btn btn-danger btn-sm" title="Delete"
                      onClick={() => handleDeleteClick("lead_sources", bucket.id)}>
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-primary primary-button"
              data-bs-toggle="modal"
              data-bs-target="#leadTypeModal"
            >
              Add Lead Type
            </button>
            <span className="badge bg-secondary">Lead Type List</span>
          </div>
          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Lead Type</th>
                <th>Lead Id</th>
                <th>Description</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {configuration?.lead_types?.map((bucket, index) => (
                <tr key={index}>
                  <td>{bucket?.name}</td>
                  <td>{bucket?.id}</td>
                  <td>{bucket?.description}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      data-bs-toggle="modal"
                      data-bs-target="#leadTypeModal"
                      onClick={() => handleEditClick("lead_types",bucket)}
                    >
                      Edit
                    </button>
                    <button type="button" class="btn btn-danger btn-sm" title="Delete"
                      onClick={() => handleDeleteClick("lead_types", bucket.id)}>
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="col">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-primary primary-button"
              data-bs-toggle="modal"
              data-bs-target="#followTypeModal"
            >
              Add FollowUp Type
            </button>
            <span className="badge bg-secondary">Follow Up List</span>
          </div>
          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Follow Up Type</th>
                <th>Follow Up Id</th>
                <th>Priority</th>
                <th>Description</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {configuration?.followup_type?.map((bucket, index) => (
                <tr key={index}>
                  <td>{bucket?.name}</td>
                  <td>{bucket?.id}</td>
                  <td>{bucket?.priority}</td>
                  <td>{bucket?.description}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      data-bs-toggle="modal"
                      data-bs-target="#followTypeModal"
                      onClick={() => handleEditClick("followup_type",bucket)}
                    >
                      Edit
                    </button>
                    <button type="button" class="btn btn-danger btn-sm" title="Delete"
                      onClick={() => handleDeleteClick("followup_type", bucket.id)}>
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-primary primary-button"
              data-bs-toggle="modal"
              data-bs-target="#priorityModal"
            >
              Add Priority
            </button>
            <span className="badge bg-secondary">Priority List</span>
          </div>
          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Priority Name</th>
                <th>Priority Id</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {configuration?.priority?.map((bucket, index) => (
                <tr key={index}>
                  <td>{bucket?.name}</td>
                  <td>{bucket?.id}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      data-bs-toggle="modal"
                      data-bs-target="#priorityModal"
                      onClick={() => handleEditClick("priority",bucket)}
                    >
                      Edit
                    </button>
                    <button type="button" class="btn btn-danger btn-sm" title="Delete"
                      onClick={() => handleDeleteClick("priority", bucket.id)}>
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="col">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-primary primary-button"
              data-bs-toggle="modal"
              data-bs-target="#bucketModal"
            >
              Add Bucket
            </button>
            <span className="badge bg-secondary">Bucket List</span>
          </div>
          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Bucket Name</th>
                <th>Bucket Lead Types</th>
                <th>Bucket Sources</th>
                <th>Action</th>
              </tr>
            </thead>
                <tbody>
                {configuration?.buckets?.map((bucket, index) => (
                    <tr key={index}>
                    <td>{bucket?.name}</td>
                    <td>{Array.isArray(bucket?.lead_types) ? bucket.lead_types.join(", ") : ''}</td>
                    <td>{Array.isArray(bucket?.source_ids) ? bucket.source_ids.join(", ") : ''}</td>
                    </tr>
                ))}
                </tbody>
          </Table>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-primary primary-button"
              data-bs-toggle="modal"
              data-bs-target="#cityModal"
            >
              Add City
            </button>
            <span className="badge bg-secondary">City List</span>
          </div>
          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>City Name</th>
                <th>City Id</th>
                <th>Is Active</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {configuration?.city?.map((bucket, index) => (
                <tr key={index}>
                  <td>{bucket?.name}</td>
                  <td>{bucket?.id}</td>
                  <td>{bucket?.is_active}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      data-bs-toggle="modal"
                      data-bs-target="#cityModal"
                      onClick={() => handleEditClick("city",bucket)}
                    >
                      Edit
                    </button>
                    <button type="button" class="btn btn-danger btn-sm" title="Delete"
                      onClick={() => handleDeleteClick("city", bucket.id)}>
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="col">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              className="btn btn-primary primary-button"
              data-bs-toggle="modal"
              data-bs-target="#countryModal"
            >
              Add Country
            </button>
            <span className="badge bg-secondary">Country List</span>
          </div>
          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Country Name</th>
                <th>Country Id</th>
                <th>Is Active</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {configuration?.country?.map((bucket, index) => (
                <tr key={index}>
                  <td>{bucket?.name}</td>
                  <td>{bucket?.id}</td>
                  <td>{bucket?.is_active}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      data-bs-toggle="modal"
                      data-bs-target="#countryModal"
                      onClick={() => handleEditClick("country",bucket)}
                    >
                      Edit
                    </button>
                    <button type="button" class="btn btn-danger btn-sm" title="Delete"
                      onClick={() => handleDeleteClick("country", bucket.id)}>
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {/* <!-----Modals----!> */}
        <div
          className="modal fade"
          id="statusModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          ref={statusModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                {isEditing ? "Edit " : "Add New "} Status
              </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleModalSubmit("lead_status", statusModalRef);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Status Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Status Name"
                      name="name"
                      value={formData.lead_status.name}
                      onChange={(e) => handleChange(e, "lead_status")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Status Description
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Status Description"
                      name="description"
                      value={formData.lead_status.description}
                      onChange={(e) => handleChange(e, "lead_status")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button btn-lg btn btn-primary w-100 rounded-pill mt-2"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal fade"
          id="followTypeModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          ref={followupModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {isEditing ? "Edit " : "Add New "} FollowUp Type
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleModalSubmit("followup_type", followupModalRef);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      FollowUp Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="FollowUp Name"
                      name="name"
                      value={formData.followup_type.name}
                      onChange={(e) => handleChange(e, "followup_type")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Description
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Description"
                      name="description"
                      value={formData.followup_type.description}
                      onChange={(e) => handleChange(e, "followup_type")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button btn-lg btn btn-primary w-100 rounded-pill mt-2"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal fade"
          id="priorityModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          ref={priorityModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {isEditing ? "Edit " : "Add New "} Priority
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleModalSubmit("priority", priorityModalRef);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Priority Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Priority Name"
                      name="name"
                      value={formData.priority.name}
                      onChange={(e) => handleChange(e, "priority")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Description
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Description"
                      name="description"
                      value={formData.priority.description}
                      onChange={(e) => handleChange(e, "priority")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button btn-lg btn btn-primary w-100 rounded-pill mt-2"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal fade"
          id="bucketModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          ref={bucketModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {isEditing ? "Edit " : "Add New "} Bucket
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleModalSubmit("buckets", bucketModalRef);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Bucket Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Bucket Name"
                      name="name"
                      value={formData.buckets.name}
                      onChange={(e) => handleChange(e, "buckets")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Bucket Lead Types
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Bucket Lead Types Comma Seprated"
                      name="lead_types"
                      value={formData.buckets.lead_types}
                      onChange={(e) => handleChange(e, "buckets")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Bucket Source Names
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Bucket Sources Comma Seprated"
                      name="source_ids"
                      value={formData.buckets.source_ids}
                      onChange={(e) => handleChange(e, "buckets")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Bucket Description
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Bucket Description"
                      name="description"
                      value={formData.buckets.description}
                      onChange={(e) => handleChange(e, "buckets")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button btn-lg btn btn-primary w-100 rounded-pill mt-2"
                  >
                    Submit
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal fade"
          id="cityModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          ref={cityModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {isEditing ? "Edit " : "Add New "} City
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleModalSubmit("city", cityModalRef);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label fw-semibold">City Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="City Name"
                      name="name"
                      value={formData.city.name}
                      onChange={(e) => handleChange(e, "city")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Country</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Country"
                      name="country"
                      value={formData.city.country}
                      onChange={(e) => handleChange(e, "city")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button btn-lg btn btn-primary w-100 rounded-pill mt-2"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal fade"
          id="countryModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          ref={countryModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {isEditing ? "Edit " : "Add New "} Country
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleModalSubmit("country", countryModalRef);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Country Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Status Name"
                      name="name"
                      value={formData.country.name}
                      onChange={(e) => handleChange(e, "country")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button btn-lg btn btn-primary w-100 rounded-pill mt-2"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal fade"
          id="leadTypeModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          ref={leadTypeModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {isEditing ? "Edit " : "Add New "} Lead Type
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleModalSubmit("lead_types", leadTypeModalRef);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Lead Type</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Lead Type"
                      name="name"
                      value={formData.lead_types.name}
                      onChange={(e) => handleChange(e, "lead_types")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Lead Type Description
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Lead Type Description"
                      name="description"
                      value={formData.lead_types.description}
                      onChange={(e) => handleChange(e, "lead_types")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button btn-lg btn btn-primary w-100 rounded-pill mt-2"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal fade"
          id="sourceModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
          ref={sourceModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {isEditing ? "Edit " : "Add New "} Source
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleModalSubmit("lead_sources", sourceModalRef);
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Source Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Source Name"
                      name="name"
                      value={formData.lead_sources.name}
                      onChange={(e) => handleChange(e, "lead_sources")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Source Priority
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Source Priority"
                      name="priority"
                      value={formData.lead_sources.priority}
                      onChange={(e) => handleChange(e, "lead_sources")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Source Bucket
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Source Bucket"
                      name="bucket"
                      value={formData.lead_sources.bucket}
                      onChange={(e) => handleChange(e, "lead_sources")}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Source Description
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Source Description"
                      name="description"
                      value={formData.lead_sources.description}
                      onChange={(e) => handleChange(e, "lead_sources")}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button btn-lg btn btn-primary w-100 rounded-pill mt-2"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 11 }}>
          <div
            ref={toastRef}
            className="toast align-items-center text-bg-success border-0"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="d-flex">
              <div className="toast-body">Lead Setting Updated Successfully !</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                data-bs-dismiss="toast"
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadSettings;
